import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover, FormControlLabel, Switch, Divider, TextField, InputAdornment } from '@mui/material';
import { ViewColumn as ColumnsIcon, Add as AddIcon, Search as SearchIcon, AutoAwesome as AIIcon } from '@mui/icons-material';

import { updateAudienceColumnVisibility } from '../../../store/Campaign';

const STANDARD_COLUMNS = [
  { name: 'name', label: 'Name' },
  { name: 'email', label: 'Email' },
  { name: 'phone', label: 'Phone' },
  { name: 'company', label: 'Company' },
];

// Property types that can be rendered as a text column in the audience table.
const INSERTABLE_PROPERTY_TYPES = new Set(['text', 'textarea', 'email', 'phone', 'url', 'number', 'currency', 'percentage', 'date', 'datetime', 'ai_variable', 'select']);

export const isColumnVisible = (visibility, name) => visibility?.[name] !== false;

// Reason: the visibility map doubles as the attach list — a key with value=false hides a column, a key with value=true (or absent for standard cols) shows it. Standard columns are visible by default; non-standard columns must be present in the map to count as attached.
export const computeAttachedAiColumns = (visibility) => Object.keys(visibility || {}).filter(k => !STANDARD_COLUMNS.find(c => c.name === k));

const ColumnsPopover = ({ anchorEl, onClose, campaignId, visibility, aiPropertyLabels, onOpenAIDrawer }) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const contactProperties = useSelector(state => state['Objects']?.['contacts']?.['properties']) || {};
  const open = Boolean(anchorEl);

  const attachedAi = useMemo(() => computeAttachedAiColumns(visibility), [visibility]);
  const attachedAll = [...STANDARD_COLUMNS, ...attachedAi.map(name => ({ name, label: aiPropertyLabels?.[name] || name, isAi: true }))];

  // Available = any insertable contact Property not already attached and not one of the four hardcoded standard columns.
  const { availableAi, availableStandard } = useMemo(() => {
    const attachedSet = new Set(Object.keys(visibility || {}));
    const standardNames = new Set(STANDARD_COLUMNS.map(c => c.name));
    const all = Object.values(contactProperties).flat().filter(p => p?.name && INSERTABLE_PROPERTY_TYPES.has(p.type) && !attachedSet.has(p.name) && !standardNames.has(p.name));
    const q = search.toLowerCase();
    const match = (p) => !search || (p.label || p.name).toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
    return {
      availableAi: all.filter(p => p.type === 'ai_variable' && match(p)),
      availableStandard: all.filter(p => p.type !== 'ai_variable' && match(p)),
    };
  }, [contactProperties, visibility, search]);

  const filteredAttached = useMemo(() => {
    if (!search) return attachedAll;
    const q = search.toLowerCase();
    return attachedAll.filter(c => c.label.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
  }, [attachedAll, search]);

  const toggle = (propertyName, isVisible) => { if (campaignId) dispatch(updateAudienceColumnVisibility({ campaignId, propertyName, isVisible })); };
  const addExisting = (propertyName) => { if (campaignId) dispatch(updateAudienceColumnVisibility({ campaignId, propertyName, isVisible: true })); };
  const createNew = () => { onClose(); if (onOpenAIDrawer) onOpenAIDrawer(); };

  return (
    <Popover
      PaperProps={{ sx: { p: 1.5, width: 320 } }}
      open={open} anchorEl={anchorEl} onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <div onClick={createNew}
        onMouseEnter={(e) => e['currentTarget']['style']['background'] = '#faf5ff'} onMouseLeave={(e) => e['currentTarget']['style']['background'] = 'transparent'}
        style={{ display: 'flex', alignItems: 'center', marginBottom: '3px', gap: 8, padding: '8px 10px', cursor: 'pointer', borderRadius: 6, fontSize: 13, color: 'var(--primary-color)', fontWeight: 500 }}
      >
        <AddIcon style={{ fontSize: 16 }} />
        Create new AI column
      </div>

      <TextField
        size="small" fullWidth placeholder="Search columns"
        value={search} onChange={(e) => setSearch(e['target']['value'])}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 16 }} /></InputAdornment>,
          sx: { height: 32, fontSize: 13, '& input': { padding: '6px 4px' } },
        }}
        sx={{ mb: 1 }}
      />

      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
        {filteredAttached.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', padding: '6px 8px 4px', textTransform: 'uppercase', letterSpacing: 0.4 }}>Visible columns</div>
            {filteredAttached.map(col => (
              <FormControlLabel
                key={col.name}
                sx={{ ml: 0, mr: 0, py: 0.25, px: 1, justifyContent: 'space-between', width: '100%' }}
                labelPlacement="start"
                control={
                  <Switch
                    size="small"
                    checked={isColumnVisible(visibility, col['name'])}
                    onChange={(e) => toggle(col['name'], e['target']['checked'])}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--primary-color)' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--primary-color)' },
                      '& .MuiSwitch-switchBase.Mui-checked:hover': { backgroundColor: 'rgba(124, 58, 237, 0.08)' },
                    }}
                  />
                }
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    {col.isAi && <AIIcon style={{ fontSize: 14, color: 'var(--primary-color)' }} />}
                    {col.label}
                  </span>
                }
              />
            ))}
          </>
        )}

        {availableAi.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', padding: '10px 8px 4px', textTransform: 'uppercase', letterSpacing: 0.4 }}>Add AI column</div>
            {availableAi.map(p => (
              <div key={p.name} onClick={() => addExisting(p.name)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer', borderRadius: 6, fontSize: 13 }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <AIIcon style={{ fontSize: 14, color: 'var(--primary-color)' }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 500 }}>{p.label || p.name}</span>
                  <span style={{ fontSize: 11, opacity: 0.6 }}>{`{{${p.name}}}`}</span>
                </div>
              </div>
            ))}
          </>
        )}

        {availableStandard.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', padding: '10px 8px 4px', textTransform: 'uppercase', letterSpacing: 0.4 }}>Add contact property</div>
            {availableStandard.map(p => (
              <div key={p.name} onClick={() => addExisting(p.name)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer', borderRadius: 6, fontSize: 13 }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 500 }}>{p.label || p.name}</span>
                  <span style={{ fontSize: 11, opacity: 0.6 }}>{p.group || p.type}</span>
                </div>
              </div>
            ))}
          </>
        )}

        {filteredAttached.length === 0 && availableAi.length === 0 && availableStandard.length === 0 && (
          <div style={{ fontSize: 12, color: '#9ca3af', padding: '12px 8px', textAlign: 'center' }}>
            {search ? 'No matches' : 'No columns available'}
          </div>
        )}
      </div>
    </Popover>
  );
};

const AudienceColumn = ({ campaignId, visibility, aiPropertyLabels, onOpenAIDrawer, className }) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <button
        className={`btn-style-white ${className || ''} ml-3`}
        onClick={(e) => setAnchor(e.currentTarget)}
        title="Columns"
      >
        <i className="icon"><ColumnsIcon style={{ fontSize: 18 }} /></i>
      </button>
      <ColumnsPopover
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        campaignId={campaignId}
        visibility={visibility}
        aiPropertyLabels={aiPropertyLabels}
        onOpenAIDrawer={onOpenAIDrawer}
      />
    </>
  );
};

export default AudienceColumn;
